import { FC, Fragment } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import Button from './Button';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    showFirstLast?: boolean;
}

export const Pagination: FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    className = '',
    showFirstLast = true,
}) => {
    // Helper to generate page numbers
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        // Always show first page
        pages.push(1);

        // Logic for ellipses and middle pages
        if (currentPage > 4) {
            pages.push('...');
        }

        let start = Math.max(2, currentPage - 1);
        let end = Math.min(totalPages - 1, currentPage + 1);

        if (currentPage <= 4) {
            end = 5;
            start = 2;
        } else if (currentPage >= totalPages - 3) {
            start = totalPages - 4;
            end = totalPages - 1;
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 3) {
            pages.push('...');
        }

        // Always show last page
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    if (totalPages <= 1) return null;

    return (
        <nav className={`flex items-center justify-center gap-1.5 ${className}`} aria-label="Pagination">
            {showFirstLast && (
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 w-8 p-0 hidden sm:flex"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    title="First Page"
                >
                    <ChevronsLeft className="w-4 h-4" />
                </Button>
            )}

            <Button
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
            >
                <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-1 mx-2">
                {getPageNumbers().map((page, index) => (
                    <Fragment key={index}>
                        {page === '...' ? (
                            <span className="text-slate-400 text-xs px-1 font-medium select-none">...</span>
                        ) : (
                            <button
                                onClick={() => onPageChange(page as number)}
                                className={`
                                    min-w-[2rem] h-8 px-3 rounded-md text-xs font-bold transition-all select-none
                                    ${currentPage === page
                                        ? 'bg-slate-900 text-white shadow-sm ring-1 ring-slate-900 cursor-default'
                                        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 hover:border-slate-300 hover:text-slate-900 active:bg-slate-100'
                                    }
                                `}
                                aria-current={currentPage === page ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        )}
                    </Fragment>
                ))}
            </div>

            <Button
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
            >
                <ChevronRight className="w-4 h-4" />
            </Button>

            {showFirstLast && (
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 w-8 p-0 hidden sm:flex"
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    title="Last Page"
                >
                    <ChevronsRight className="w-4 h-4" />
                </Button>
            )}
        </nav>
    );
};

export default Pagination; // Default export for index.ts re-export compatibility if needed, or named
